import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const number = value => value == null || typeof value === 'boolean' || String(value).trim() === '' || !Number.isFinite(Number(value)) ? null : Number(value);
const fmt = value => number(value) == null ? '-' : Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 });
const sum = values => values.reduce((total, value) => total + value, 0);
const text = value => value == null || value === '' ? '-' : String(value);
export const formatReportHour = (hour, minute = '00') => `${hour % 12 || 12}:${minute} ${hour >= 12 ? 'PM' : 'AM'}`;

// Resolve applied filter IDs against the same permitted hierarchy used by the dropdowns.
export function managementScopeNames(filters = {}, hierarchy = []) {
  const fields = ['site_id', 'location_id', 'sublocation_id'];
  const names = Object.fromEntries(fields.map((key, index) => {
    if (filters[key] == null || filters[key] === '') return [key, 'All'];
    const row = hierarchy.find(entry => fields.slice(0, index + 1).every(parent =>
      filters[parent] == null || filters[parent] === '' || String(entry[parent]) === String(filters[parent])));
    return [key, row?.[key.replace('_id', '_name')] || 'Name unavailable'];
  }));
  const screen = hierarchy.find(row => String(row.station_id) === String(filters.station)
    && fields.every(key => filters[key] == null || filters[key] === '' || String(row[key]) === String(filters[key])));
  names.station = filters.station == null || filters.station === '' ? 'All' : screen?.station_name || 'Name unavailable';
  return names;
}

// Use individual valid lifecycle samples, never an unweighted average of daily averages.
export function buildManagementSummary({ days = [], timings = [], items = [], hours = [] }) {
  const samples = timings.map(row => number(row.bump_minutes)).filter(value => value != null && value >= 0).sort((a, b) => a - b);
  const counts = days.map(row => number(row.total_orders)).filter(value => value != null && value >= 0);
  const middle = Math.floor(samples.length / 2);
  const hourly = new Map();
  hours.forEach(row => {
    const hour = number(row.hour_of_day), count = number(row.total_orders);
    if (Number.isInteger(hour) && hour >= 0 && hour <= 23 && count != null && count >= 0) hourly.set(hour, (hourly.get(hour) || 0) + count);
  });
  const totalOrders = counts.length ? sum(counts) : null;
  const totalItems = number(items[0]?.total_items_sold);
  return { totalOrders,
    totalItems, sampleCount: samples.length,
    averageItemsPerOrder: totalOrders > 0 && totalItems != null ? totalItems / totalOrders : null,
    average: samples.length ? sum(samples) / samples.length : null,
    median: samples.length ? (samples[middle] + samples[Math.floor((samples.length - 1) / 2)]) / 2 : null,
    hours: [...hourly].sort((a, b) => b[1] - a[1] || a[0] - b[0]),
    items: items.filter(row => number(row.total_quantity) != null).sort((a, b) => Number(b.total_quantity) - Number(a.total_quantity)),
  };
}

// A curated operational summary; backend timing definitions and performance classifications remain authoritative.
export function createManagementReport({ report, startDate, endDate }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const summary = buildManagementSummary(report);
  const margin = 38, width = doc.internal.pageSize.getWidth() - margin * 2;
  let y = 0;
  const ensureSpace = height => { if (y + height > doc.internal.pageSize.getHeight() - 48) { doc.addPage(); y = 43; } };
  const paragraph = (value, size = 10) => {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(size); doc.setTextColor(75, 91, 111);
    const lines = doc.splitTextToSize(value, width); ensureSpace(lines.length * (size + 3) + 10); doc.text(lines, margin, y); y += lines.length * (size + 3) + 10;
  };
  const heading = value => { ensureSpace(85); doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(23, 43, 70); doc.text(value, margin, y); y += 17; };
  const page = (title, subtitle) => {
    y = 43; doc.setFont('helvetica', 'bold'); doc.setFontSize(21); doc.setTextColor(23, 43, 70); doc.text(title, margin, y); y += 23;
    paragraph(`${startDate} to ${endDate} | ${subtitle}`);
  };
  // Vector KPI cards stay sharp when printed; captions clarify which ticket-time measure is shown.
  const cards = entries => {
    const gap = 10, cardWidth = (width - gap * 2) / 3, cardHeight = 76;
    const colors = [[49, 95, 202], [14, 132, 119], [120, 85, 181]];
    ensureSpace(Math.ceil(entries.length / 3) * (cardHeight + gap));
    entries.forEach(([label, value, caption], index) => {
      const x = margin + (index % 3) * (cardWidth + gap), top = y + Math.floor(index / 3) * (cardHeight + gap);
      doc.setFillColor(243, 247, 252); doc.setDrawColor(221, 229, 238);
      doc.roundedRect(x, top, cardWidth, cardHeight, 7, 7, 'FD');
      doc.setFillColor(...colors[index % 3]); doc.roundedRect(x + 10, top + 12, 3, 49, 1, 1, 'F');
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(75, 91, 111);
      doc.text(label, x + 21, top + 21);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(20); doc.setTextColor(23, 43, 70);
      // Keep large client totals inside the card without reducing the size of other KPIs.
      if (doc.getTextWidth(value) > cardWidth - 31) doc.setFontSize(20 * (cardWidth - 31) / doc.getTextWidth(value));
      doc.text(value, x + 21, top + 45);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(75, 91, 111);
      if (caption) doc.text(caption, x + 21, top + 62);
    });
    y += Math.ceil(entries.length / 3) * (cardHeight + gap) + 7;
  };
  const table = (headers, rows) => {
    autoTable(doc, { startY: y, margin: { left: margin, right: margin, top: 38, bottom: 42 },
      head: [headers], body: rows.length ? rows : [headers.map((_, i) => i === 0 ? 'No available data' : '-')],
      styles: { font: 'helvetica', fontSize: 9, cellPadding: 4.5, overflow: 'linebreak' },
      headStyles: { fillColor: [49, 95, 202], fontStyle: 'bold' }, alternateRowStyles: { fillColor: [243, 247, 252] },
      ...(headers.length === 6 ? { columnStyles: { 0: { cellWidth: width * 0.34 }, 1: { cellWidth: width * 0.10 },
        2: { cellWidth: width * 0.11 }, 3: { cellWidth: width * 0.15 }, 4: { cellWidth: width * 0.14 }, 5: { cellWidth: width * 0.16 } } } : {}),
      rowPageBreak: 'avoid' });
    y = doc.lastAutoTable.finalY + 23;
  };
  // Print-ready horizontal bars: zero-based scale, full labels and exact values require no hover.
  const bars = (rows, colors = [[49, 95, 202]], unit = 'orders', labelWidth = 125) => {
    if (!rows.length) { paragraph('No available data.'); return; }
    const valueWidth = 65, plotWidth = width - labelWidth - valueWidth;
    const max = Math.max(...rows.map(([, value]) => value), 1);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
    const labels = rows.map(([label]) => doc.splitTextToSize(label, labelWidth - 10));
    const heights = labels.map(lines => Math.max(24, lines.length * 11 + 8));
    ensureSpace(sum(heights) + 25);
    rows.forEach(([label, value], index) => {
      const baseline = y;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(75, 91, 111);
      doc.text(labels[index], margin, baseline + 11);
      doc.setFillColor(237, 243, 250); doc.rect(margin + labelWidth, baseline, plotWidth, 15, 'F');
      doc.setFillColor(...colors[index % colors.length]);
      if (value > 0) doc.rect(margin + labelWidth, baseline, plotWidth * value / max, 15, 'F');
      doc.setFont('helvetica', 'bold'); doc.setTextColor(23, 43, 70);
      doc.text(fmt(value), margin + width, baseline + 11, { align: 'right' });
      y += heights[index];
    });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(75, 91, 111);
    doc.text('0', margin + labelWidth, y + 3);
    doc.text(`${fmt(max)} ${unit}`, margin + labelWidth + plotWidth, y + 3, { align: 'right' });
    y += 25;
  };
  page('MyODS Management Report', 'Kitchen Overview');
  const labels = { site_id: 'Site', location_id: 'Location', sublocation_id: 'Sublocation', station: 'Screen', daypart: 'Meal period' };
  const scopeNames = managementScopeNames(report.filters, report.hierarchy);
  paragraph(Object.entries(labels).map(([key, label]) => `${label}: ${scopeNames[key] ?? (report.filters?.[key] || 'All')}`).join(' | '), 9);
  heading('KPI Metrics');
  cards([['Orders received', fmt(summary.totalOrders)],
    ['Items ordered', fmt(summary.totalItems)],
    ['Avg. items per order', fmt(summary.averageItemsPerOrder)],
    ['Average active time', fmt(summary.average)],
    ['Median active time', fmt(summary.median)],
    ['Peak-hour orders', fmt(summary.hours[0]?.[1]), summary.hours.length ? `${formatReportHour(summary.hours[0][0])}` : 'No available hourly data']]);
  heading('Busiest Dates - Top 10');
  bars([...report.days].filter(row => number(row.total_orders) != null && number(row.total_orders) >= 0)
    .sort((a, b) => Number(b.total_orders) - Number(a.total_orders)).slice(0, 10).map(row => [text(row.date).slice(0, 10), Number(row.total_orders)]));
  heading('Busiest Hours - Top 3');
  table(['Local hour', 'Orders across selected period'], summary.hours.slice(0, 3).map(([hour, count]) => [`${formatReportHour(hour)} - ${formatReportHour(hour, '59')}`, fmt(count)]));

  doc.addPage(); page('Ticket performance', 'Ticket Overview');
  const statuses = ['Normal', 'Needs Review', 'Outlier', 'Insufficient Data'];
  heading('Ticket Performance');
  bars(statuses.map(status => [status, report.timings.filter(row => row.performance_status === status).length])
    .concat([['Unclassified / other', report.timings.filter(row => !statuses.includes(row.performance_status)).length]]),
  [[14, 132, 119], [171, 112, 20], [189, 65, 75], [112, 123, 141], [112, 123, 141]], 'tickets');
  paragraph('Ratings compare tickets with your kitchen baseline. Insufficient Data means there is not enough history to rate performance. Timing rules are still under review.', 9);
  heading('Tickets to Review - Top 15');
  const ticketMap = new Map(report.tickets.map(row => [String(row.order_id), row]));
  const flagged = report.timings.filter(row => ['Needs Review', 'Outlier'].includes(row.performance_status))
    .sort((a, b) => (number(b.bump_minutes) ?? -1) - (number(a.bump_minutes) ?? -1));
  table(['Order', 'Table', 'Active min', 'Total min', 'Performance'], flagged.slice(0, 15).map(row => [text(row.order_id),
    text(ticketMap.get(String(row.order_id))?.table_name), fmt(row.bump_minutes), fmt(row.ticket_minutes), text(row.performance_status)]));
  paragraph(`${flagged.length} tickets flagged; the 15 longest active times are shown when available. Export the Orders table for a more specific list.`, 9);

  doc.addPage(); page('Menu performance', 'Items Ordered');
  paragraph('Units show how much was ordered. Orders show how many tickets included each item.', 10);
  const itemRows = rows => rows.map(row => {
    const orders = number(row.distinct_orders), quantity = number(row.total_quantity), elapsed = number(row.average_bump_time_seconds);
    return [text(row.name), fmt(quantity), fmt(orders), orders > 0 ? fmt(quantity / orders) : '-',
      summary.totalOrders > 0 && orders != null ? `${fmt(orders / summary.totalOrders * 100)}%` : '-', elapsed != null ? fmt(elapsed / 60) : '-'];
  });
  // Wrap full item names so the printed chart can be read independently of the table.
  heading('Top 5 Most Ordered - Units');
  bars(summary.items.slice(0, 5).map(row => [text(row.name), Math.max(0, Number(row.total_quantity))]), [[14, 132, 119]], 'units', 200);
  heading('Top 10 Most Ordered');
  table(['Item', 'Units', 'Orders', 'Units / order', '% orders', 'Recorded min'], itemRows(summary.items.slice(0, 10)));
  heading('Top 5 Least Ordered');
  table(['Item', 'Units', 'Orders', 'Units / order', '% orders', 'Recorded min'], itemRows([...summary.items].reverse().slice(0, 5)));
  paragraph('Units / order: quantity per ticket containing that item.\n% orders: share of all tickets containing it; shares can total over 100%.\nRecorded min: average item elapsed time, not pause-adjusted ticket time.', 9);
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p += 1) {
    doc.setPage(p); doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(88, 107, 130);
    doc.text(`MyODS Management Report | Generated ${new Date().toISOString().slice(0, 10)} | ${p} / ${pages}`, margin, doc.internal.pageSize.getHeight() - 20);
  }
  return doc;
}

export function exportManagementReport(options) {
  const name = Array.from(options.fileName).map(char => char.charCodeAt(0) < 32 ? '-' : char).join('').replace(/[<>:"/\\|?*]/g, '-').replace(/\.pdf$/i, '');
  createManagementReport(options).save(`${name || 'Management-report'}.pdf`);
}
